// Phone-number inventory and tenant cap management are profile-invariant.
// Carrier search, purchase, and release depend on number_provisioning_enabled.
// Self-managed installs populate inventory from customer-private SIP trunks.

import { useQuery } from "@tanstack/react-query";

import type { components } from "../api/schema";
import { apiClient } from "./apiClient";
import { ApiRequestError } from "./errors";
import { MAX_PAGE_SIZE } from "./pagination";
import { queryKeys } from "./queryKeys";

export type PhoneNumber = components["schemas"]["PhoneNumberResponse"];
export type PhoneNumberSearchResult =
  components["schemas"]["PhoneNumberSearchResult"];

export interface NumberProvisioning {
  enabled: boolean;
  isError: boolean;
  isPending: boolean;
}

async function getMe() {
  const { data, error } = await apiClient.GET("/v1/me");
  if (error) {
    throw new ApiRequestError(error);
  }
  return data;
}

// The capability gate read from GET /v1/me. The console never receives or
// interprets an install-profile label (docs/07 §5).
export function useNumberProvisioning(): NumberProvisioning {
  const me = useQuery({ queryKey: queryKeys.me, queryFn: getMe });

  return {
    enabled: me.data?.number_provisioning_enabled === true,
    isError: me.isError,
    isPending: me.isPending,
  };
}

// The COMPLETE inventory, every keyset page walked to exhaustion at the
// server's max page size. Consumers split this list by ownership (the
// settings screen's owned/shared sections, the persona picker's pinnable
// filter), and org-owned rows sort first while disabled history accumulates —
// so a single-page read could silently omit the entire shared pool.
export async function listPhoneNumbers(): Promise<PhoneNumber[]> {
  const numbers: PhoneNumber[] = [];
  let pageToken: string | undefined;
  do {
    const { data, error } = await apiClient.GET("/v1/phone-numbers", {
      params: {
        query:
          pageToken === undefined
            ? { page_size: MAX_PAGE_SIZE }
            : { page_size: MAX_PAGE_SIZE, page_token: pageToken },
      },
    });
    if (error) {
      throw new ApiRequestError(error);
    }
    numbers.push(...data.items);
    pageToken = data.next_page_token ?? undefined;
  } while (pageToken !== undefined);
  return numbers;
}

export async function searchAvailableNumbers(
  areaCode: string,
): Promise<PhoneNumberSearchResult[]> {
  const { data, error } = await apiClient.GET("/v1/phone-numbers/available", {
    params: {
      query: areaCode === "" ? {} : { area_code: areaCode },
    },
  });
  if (error) {
    throw new ApiRequestError(error);
  }
  return [...data.items];
}

export async function purchasePhoneNumber(
  address: string,
): Promise<PhoneNumber> {
  const { data, error } = await apiClient.POST("/v1/phone-numbers", {
    body: { address, area_code: null },
    headers: { "Idempotency-Key": window.crypto.randomUUID() },
  });
  if (error) {
    throw new ApiRequestError(error);
  }
  return data;
}

// Platform-owned shared inventory (`shared`) is visible to every org and
// leasable by `auto` persona selection, but it is not this org's number: it
// cannot be pinned or released, and it does not consume the purchase cap.
export function isSharedNumber(number: PhoneNumber): boolean {
  return number.shared;
}

// The contract the generated type cannot express: a positive integer
// `shared_ordinal` exactly when `shared`. `PhoneNumberResponse` permits
// `shared=true` with `shared_ordinal=null`, so the invariant is enforced
// here at the parsing boundary instead.
function hasValidSharedContract(number: PhoneNumber): boolean {
  const ordinal = number.shared_ordinal;
  return number.shared
    ? ordinal !== null && Number.isInteger(ordinal) && ordinal > 0
    : ordinal === null;
}

// A shared number reads as generic inventory, not as this organization's
// identity, so the list names it `Number N` and puts the address one `i`
// away. `shared_ordinal` is the server's position over the shared pool's
// FULL ordering, so the label does not renumber across pages or when another
// org buys a number. docs/09 keeps a shared address behind `HelpTip`, so a
// contract-invalid row (shared without a positive ordinal, or an ordinal on
// an org-owned row) renders a generic shared-number label — NEVER the
// address, whose ownership the broken contract leaves in doubt.
export function phoneNumberLabel(number: PhoneNumber): string {
  if (!hasValidSharedContract(number)) {
    return "Shared number";
  }
  return number.shared
    ? `Number ${String(number.shared_ordinal)}`
    : number.address;
}

export async function releasePhoneNumber(id: string): Promise<void> {
  const { error } = await apiClient.DELETE("/v1/phone-numbers/{id}", {
    params: { path: { id } },
  });
  if (error) {
    throw new ApiRequestError(error);
  }
}

export async function patchPhoneNumber(
  id: string,
  maxConcurrentCalls: number,
): Promise<PhoneNumber> {
  const { data, error } = await apiClient.PATCH("/v1/phone-numbers/{id}", {
    params: { path: { id } },
    body: { max_concurrent_calls: maxConcurrentCalls },
  });
  if (error) throw new ApiRequestError(error);
  return data;
}
